import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ScanSearch, Upload, Loader2, Trash2, Plus, FileDown, Mail, X, ArrowLeft,
  FolderPlus, Layers, MapPin, MessageSquare, History, ClipboardList,
  AlertTriangle, CheckCircle2, Eye, ZoomIn, ZoomOut, RefreshCw, Crosshair, Zap, DollarSign, Cable,
  LayoutDashboard, Calculator, Network, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { api } from '../../utils/api';
import { useLang } from '../../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEVICE_TYPES = [
  'duplex_outlet', 'gfci_outlet', 'dedicated_outlet', 'exterior_outlet', 'outlet_220v',
  'switch', 'switch_3way', 'switch_4way', 'dimmer',
  'light_fixture', 'recessed_light', 'ceiling_fan', 'wall_sconce', 'emergency_light', 'exit_sign',
  'smoke_detector', 'co_detector', 'junction_box', 'panel', 'disconnect',
  'hvac_connection', 'appliance_connection', 'low_voltage', 'camera', 'data_jack', 'other'
];

const typeLabel = (t) => (t || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const confLabel = (c) => c >= 99 ? 'Very High' : c >= 95 ? 'High' : c >= 85 ? 'Medium' : 'Needs Review';

const MARKER_COLORS = { verified: '#22c55e', ai_detected: '#3b82f6', needs_review: '#eab308', issue: '#ef4444' };

const STATUS_BADGE = {
  PASS: 'bg-green-500/15 text-green-400 border-green-500/30',
  FAIL: 'bg-red-500/15 text-red-400 border-red-500/30',
  WARNING: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  NEEDS_REVIEW: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  RESOLVED: 'bg-blue-500/15 text-blue-400 border-blue-500/30'
};

const DISCIPLINE_META = {
  electrical_power: { label: 'Power', cls: 'bg-red-500/20 text-red-400' },
  lighting: { label: 'Lighting', cls: 'bg-yellow-500/20 text-yellow-400' },
  low_voltage: { label: 'Low Voltage', cls: 'bg-blue-500/20 text-blue-400' },
  fire_alarm: { label: 'Fire Alarm', cls: 'bg-orange-500/20 text-orange-400' },
  security: { label: 'Security', cls: 'bg-purple-500/20 text-purple-400' },
  architectural: { label: 'Arch', cls: 'bg-gray-600/40 text-gray-300' },
  plumbing: { label: 'Plumbing', cls: 'bg-cyan-500/20 text-cyan-400' },
  mechanical: { label: 'Mech', cls: 'bg-teal-500/20 text-teal-400' },
  structural: { label: 'Struct', cls: 'bg-stone-500/20 text-stone-300' },
  site: { label: 'Site', cls: 'bg-green-500/20 text-green-400' },
  title_sheet: { label: 'Title', cls: 'bg-gray-600/40 text-gray-300' },
  other: { label: 'Other', cls: 'bg-gray-700 text-gray-400' }
};

const PHASE_LABELS = { starting: 'Starting', legend_scan: 'Legend scan', page_analysis: 'Page analysis', finalizing: 'Finalizing' };

const PROJECT_STATUS = {
  created: { label: 'Created', cls: 'bg-gray-700 text-gray-300' },
  uploaded: { label: 'Uploaded', cls: 'bg-blue-500/20 text-blue-400' },
  processing: { label: 'AI Analyzing...', cls: 'bg-yellow-500/20 text-yellow-400' },
  analyzed: { label: 'Analyzed', cls: 'bg-green-500/20 text-green-400' },
  error: { label: 'Error', cls: 'bg-red-500/20 text-red-400' }
};

/* ---------------- PDF report ---------------- */
const buildReport = (project, boq) => {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const W = doc.internal.pageSize.getWidth();
  const M = 40;
  let y;

  // Cover
  doc.setFillColor(9, 9, 11); doc.rect(0, 0, W, 792, 'F');
  doc.setTextColor(227, 27, 35); doc.setFont('helvetica', 'bold'); doc.setFontSize(26);
  doc.text('BLUEPRINT READER AI', M, 180);
  doc.setTextColor(255, 255, 255); doc.setFontSize(15);
  doc.text('Electrical Blueprint Analysis Report', M, 210);
  doc.setFontSize(11); doc.setFont('helvetica', 'normal'); doc.setTextColor(200, 200, 200);
  let cy = 280;
  [['Project', project.name], ['Address', project.address], ['Client', project.client],
   ['Project #', project.project_number], ['Blueprint Version', `v${project.current_version}`],
   ['Pages', String(project.page_count)], ['Report Date', new Date().toLocaleString()],
   ['Square Footage', project.total_sqft?.value ? `${project.total_sqft.value} sqft (${project.total_sqft.source})` : 'Unable to verify'],
   ['Scale', project.scale || 'Not stated']].forEach(([k, v]) => {
    if (!v) return;
    doc.setFont('helvetica', 'bold'); doc.text(`${k}:`, M, cy);
    doc.setFont('helvetica', 'normal'); doc.text(String(v), M + 130, cy); cy += 22;
  });
  doc.setTextColor(120, 120, 120); doc.setFontSize(9);
  doc.text('BELLAJA Electrical & Security Systems — NYC & Connecticut', M, 720);

  const newPage = (title) => {
    doc.addPage(); y = 60;
    doc.setFillColor(227, 27, 35); doc.rect(0, 0, W, 6, 'F');
    doc.setTextColor(20, 20, 20); doc.setFont('helvetica', 'bold'); doc.setFontSize(14);
    doc.text(title, M, y); y += 24;
  };
  const guard = (needed, title) => { if (y + needed > 740) newPage(title + ' (cont.)'); };

  // Summary
  newPage('Project Summary');
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(60, 60, 60);
  [[`Total devices detected`, project.device_count],
   [`Code checks - PASS`, project.passes], [`Code issues - FAIL`, project.issues],
   [`Warnings`, project.warnings], [`Needs review`, project.needs_review]].forEach(([k, v]) => {
    doc.text(`${k}: ${v ?? 0}`, M, y); y += 16;
  });

  // Takeoff
  newPage('Electrical Takeoff');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('ITEM', M, y); doc.text('QTY', 330, y, { align: 'right' });
  doc.text('VERIFIED', 420, y, { align: 'right' }); doc.text('NEEDS REVIEW', 530, y, { align: 'right' });
  y += 6; doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 14;
  doc.setFont('helvetica', 'normal');
  (project.takeoff || []).forEach(r => {
    guard(16, 'Electrical Takeoff');
    doc.text(typeLabel(r.type), M, y); doc.text(String(r.total), 330, y, { align: 'right' });
    doc.text(String(r.verified), 420, y, { align: 'right' }); doc.text(String(r.needs_review), 530, y, { align: 'right' });
    y += 15;
  });

  // Bill of Quantities
  if (boq && ((boq.device_lines || []).length || (boq.wire_lines || []).length)) {
    newPage('Bill of Quantities');
    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text('ITEM', M, y); doc.text('QTY', 300, y, { align: 'right' });
    doc.text('MAT $/U', 380, y, { align: 'right' }); doc.text('LAB $/U', 455, y, { align: 'right' });
    doc.text('TOTAL', 540, y, { align: 'right' });
    y += 6; doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 14;
    doc.setFont('helvetica', 'normal');
    [...(boq.device_lines || []), ...(boq.wire_lines || [])].forEach(l => {
      guard(16, 'Bill of Quantities');
      doc.text(String(l.description).substring(0, 46), M, y);
      doc.text(`${l.qty} ${l.unit}`, 300, y, { align: 'right' });
      doc.text(`$${l.material_unit}`, 380, y, { align: 'right' });
      doc.text(`$${l.labor_unit}`, 455, y, { align: 'right' });
      doc.text(`$${(l.line_total || 0).toFixed(2)}`, 540, y, { align: 'right' });
      y += 15;
    });
    guard(60, 'Bill of Quantities');
    y += 8; doc.setFont('helvetica', 'bold');
    doc.text(`Material subtotal: $${(boq.subtotal_material || 0).toFixed(2)}`, M, y); y += 15;
    doc.text(`Labor subtotal: $${(boq.subtotal_labor || 0).toFixed(2)}`, M, y); y += 15;
    doc.setTextColor(227, 27, 35); doc.setFontSize(11);
    doc.text(`GRAND TOTAL: $${(boq.grand_total || 0).toFixed(2)}`, M, y);
    doc.setTextColor(60, 60, 60); doc.setFontSize(10); doc.setFont('helvetica', 'normal');
  }

  // Findings
  newPage('Code Compliance Findings');
  (project.findings || []).forEach(f => {
    guard(80, 'Code Compliance Findings');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(20, 20, 20);
    doc.text(`[${f.status}] ${f.title}`, M, y); y += 13;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(80, 80, 80);
    const lines = [
      `Code: ${f.code_name} ${f.code_edition} §${f.code_section} | Room: ${f.room || '-'} | Page: ${f.page} | Confidence: ${f.confidence}%`,
      f.measured_value ? `Measured: ${f.measured_value} | Required: ${f.required_value || '-'}` : null,
      `Requirement: ${f.requirement}`, `Explanation: ${f.explanation}`, `Evidence: ${f.evidence}`,
      f.admin_decision ? `Admin decision: ${f.admin_decision} ${f.admin_comment ? '- ' + f.admin_comment : ''}` : null
    ].filter(Boolean);
    lines.forEach(l => {
      const wrapped = doc.splitTextToSize(l, W - 2 * M);
      guard(wrapped.length * 10 + 4, 'Code Compliance Findings');
      doc.text(wrapped, M, y); y += wrapped.length * 10 + 2;
    });
    y += 8;
  });

  // Device schedule
  newPage('Electrical Device Schedule');
  doc.setFontSize(8); doc.setFont('helvetica', 'bold');
  doc.text('TYPE', M, y); doc.text('ROOM', 210, y); doc.text('PAGE', 330, y);
  doc.text('SOURCE', 380, y); doc.text('CONF.', 460, y); doc.text('STATUS', 510, y);
  y += 6; doc.line(M, y, W - M, y); y += 12;
  doc.setFont('helvetica', 'normal');
  (project.devices || []).forEach(d => {
    guard(13, 'Electrical Device Schedule');
    doc.text(typeLabel(d.type).slice(0, 30), M, y); doc.text((d.room || '-').slice(0, 20), 210, y);
    doc.text(String(d.page), 330, y); doc.text(d.source || '-', 380, y);
    doc.text(`${d.confidence ?? '-'}%`, 460, y); doc.text(d.status || '-', 510, y);
    y += 12;
  });

  // Notes
  if ((project.ai_notes || []).length) {
    newPage('AI Notes & Observations');
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(70, 70, 70);
    project.ai_notes.forEach(n => {
      const wrapped = doc.splitTextToSize(`• ${n}`, W - 2 * M);
      guard(wrapped.length * 11 + 3, 'AI Notes');
      doc.text(wrapped, M, y); y += wrapped.length * 11 + 3;
    });
  }
  return doc;
};

/* ---------------- Dashboard ---------------- */
const ProjectDashboard = ({ onOpen }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', client: '', project_number: '' });

  const load = useCallback(async () => {
    try { setProjects(await api('/api/admin/blueprint-ai/projects')); } catch { /* */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name) { toast.error('Project name is required'); return; }
    try {
      const p = await api('/api/admin/blueprint-ai/projects', { method: 'POST', body: JSON.stringify(form) });
      setShowNew(false); setForm({ name: '', address: '', client: '', project_number: '' });
      onOpen(p.id);
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project and all its data?')) return;
    try { await api(`/api/admin/blueprint-ai/projects/${id}`, { method: 'DELETE' }); load(); toast.success('Project deleted'); }
    catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-6" data-testid="bpai-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3">
            <ScanSearch className="w-8 h-8 text-red-500" /> Blueprint Reader AI
          </h1>
          <p className="text-sm text-gray-500 mt-1">Upload. Analyze. Verify. Measure. Review.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary flex items-center gap-2" data-testid="bpai-new-project-btn">
          <FolderPlus className="w-4 h-4" /> New Project
        </button>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div> : (
        projects.length === 0 ? (
          <div className="card p-14 text-center text-gray-500" data-testid="bpai-empty">
            <ScanSearch className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p className="text-white font-semibold mb-1">No projects yet</p>
            <p className="text-sm">Create a project, upload a blueprint PDF, and let the AI analyze every electrical detail.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {projects.map(p => {
              const st = PROJECT_STATUS[p.status] || PROJECT_STATUS.created;
              return (
                <div key={p.id} onClick={() => onOpen(p.id)} className="card p-5 cursor-pointer hover:border-red-500/50 transition-colors" data-testid={`bpai-project-${p.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-white font-bold">{p.name}</h3>
                      <p className="text-xs text-gray-500">{p.address || '—'} {p.client ? `· ${p.client}` : ''}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center mb-3">
                    <div><p className="text-lg font-bold text-white">{p.page_count}</p><p className="text-[10px] text-gray-500">PAGES</p></div>
                    <div><p className="text-lg font-bold text-blue-400">{p.device_count}</p><p className="text-[10px] text-gray-500">DEVICES</p></div>
                    <div><p className="text-lg font-bold text-red-400">{p.issues}</p><p className="text-[10px] text-gray-500">ISSUES</p></div>
                    <div><p className="text-lg font-bold text-yellow-400">{p.warnings + p.needs_review}</p><p className="text-[10px] text-gray-500">REVIEW</p></div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-gray-500">
                    <span>{p.updated_at ? new Date(p.updated_at).toLocaleString() : ''} {p.last_editor ? `· ${p.last_editor}` : ''}</span>
                    <button onClick={(e) => remove(e, p.id)} className="text-red-400 p-1" data-testid={`bpai-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {showNew && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="card p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="bpai-new-modal">
            <h3 className="text-lg font-bold text-white mb-4">New Blueprint Project</h3>
            <div className="space-y-3">
              <input className="input" placeholder="Project name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="bpai-form-name" />
              <input className="input" placeholder="Project address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="bpai-form-address" />
              <input className="input" placeholder="Client" value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} />
              <input className="input" placeholder="Project number" value={form.project_number} onChange={e => setForm({ ...form, project_number: e.target.value })} />
            </div>
            <button onClick={create} className="btn-primary w-full mt-4" data-testid="bpai-create-btn">Create Project</button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- Project Workspace ---------------- */
const ProjectWorkspace = ({ projectId, onBack }) => {
  const fileRef = useRef(null);
  const [project, setProject] = useState(null);
  const [page, setPage] = useState(1);
  const [images, setImages] = useState({});
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('takeoff');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [markerFilter, setMarkerFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [addMode, setAddMode] = useState(false);
  const [findingFilter, setFindingFilter] = useState('all');
  const [askQ, setAskQ] = useState('');
  const [askLog, setAskLog] = useState([]);
  const [asking, setAsking] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState({ to_email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [compare, setCompare] = useState(null);
  const [circuitFilter, setCircuitFilter] = useState(null);
  const [disciplineFilter, setDisciplineFilter] = useState('all');
  const [boq, setBoq] = useState(null);
  const [wiring, setWiring] = useState(null);
  const [calcs, setCalcs] = useState(null);
  const pollRef = useRef(null);

  const loadCalcs = async () => {
    try { setCalcs(await api(`/api/admin/blueprint-ai/projects/${projectId}/calculations`)); } catch (e) { toast.error(e.message); }
  };

  const loadBoq = async () => {
    try { setBoq(await api(`/api/admin/blueprint-ai/projects/${projectId}/boq`)); } catch (e) { toast.error(e.message); }
  };

  const loadWiring = async () => {
    try { setWiring(await api(`/api/admin/blueprint-ai/projects/${projectId}/wiring`)); } catch (e) { toast.error(e.message); }
  };

  const savePrice = async (line, field, value) => {
    const body = { material_cost: line.material_unit, labor_cost: line.labor_unit, [field]: parseFloat(value) || 0 };
    try {
      await api(`/api/admin/blueprint-ai/price-catalog/${line.item_id}`, { method: 'PUT', body: JSON.stringify(body) });
      await loadBoq();
      toast.success('Price updated');
    } catch (e) { toast.error(e.message); }
  };

  const loadCompare = async () => {
    const vs = project?.versions || [];
    if (vs.length < 2) return;
    try {
      const r = await api(`/api/admin/blueprint-ai/projects/${projectId}/compare?v_from=${vs[vs.length - 2].version}&v_to=${vs[vs.length - 1].version}`);
      setCompare(r);
    } catch (e) { toast.error(e.message); }
  };

  const load = useCallback(async () => {
    try {
      const p = await api(`/api/admin/blueprint-ai/projects/${projectId}`);
      setProject(p);
      return p;
    } catch (e) { toast.error(e.message); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (project?.status === 'processing' && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const p = await load();
        if (p && p.status !== 'processing') {
          clearInterval(pollRef.current); pollRef.current = null;
          if (p.status === 'analyzed') toast.success('AI analysis complete!');
          if (p.status === 'error') toast.error(`Analysis failed: ${p.analysis_error}`);
        }
      }, 4000);
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [project?.status, load]);

  const loadImage = useCallback(async (pg) => {
    const key = `${project?.current_version}-${pg}`;
    if (!project || images[key]) return;
    try {
      const r = await api(`/api/admin/blueprint-ai/projects/${projectId}/pages/${pg}/image`);
      setImages(prev => ({ ...prev, [key]: r.image_base64 }));
    } catch { /* */ }
  }, [project, images, projectId]);

  useEffect(() => { if (project?.page_count > 0) loadImage(page); }, [project, page, loadImage]);

  useEffect(() => {
    if (project?.status !== 'analyzed') return;
    if (tab === 'overview') {
      if (!boq) loadBoq();
      if (!wiring) loadWiring();
      if (!calcs) loadCalcs();
    }
    if (tab === 'calcs' && !calcs) loadCalcs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, project?.status]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/blueprint-ai/projects/${projectId}/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      toast.success(`Uploaded v${data.version} (${data.pages} pages)`);
      setImages({}); setPage(1);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const analyze = async () => {
    try {
      await api(`/api/admin/blueprint-ai/projects/${projectId}/analyze`, { method: 'POST' });
      toast.info('AI analysis started — this may take a few minutes.');
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const saveDevice = async (updates, comment = '') => {
    try {
      const d = await api(`/api/admin/blueprint-ai/devices/${selectedDevice.id}`, {
        method: 'PUT', body: JSON.stringify({ ...updates, comment })
      });
      setSelectedDevice(d);
      await load();
      toast.success('Device updated');
    } catch (e) { toast.error(e.message); }
  };

  const deleteDevice = async () => {
    if (!window.confirm('Delete this device?')) return;
    try {
      await api(`/api/admin/blueprint-ai/devices/${selectedDevice.id}`, { method: 'DELETE' });
      setSelectedDevice(null); await load(); toast.success('Device deleted');
    } catch (e) { toast.error(e.message); }
  };

  const planClick = async (e) => {
    if (!addMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    try {
      const d = await api(`/api/admin/blueprint-ai/projects/${projectId}/devices`, {
        method: 'POST',
        body: JSON.stringify({ type: 'duplex_outlet', page, x_pct: Math.round(x * 10) / 10, y_pct: Math.round(y * 10) / 10 })
      });
      setAddMode(false); setSelectedDevice(d); setTab('device');
      await load(); toast.success('Device added — edit its details on the right');
    } catch (err) { toast.error(err.message); }
  };

  const updateFinding = async (f, updates) => {
    try {
      await api(`/api/admin/blueprint-ai/findings/${f.id}`, { method: 'PUT', body: JSON.stringify(updates) });
      await load();
      toast.success('Finding updated');
    } catch (e) { toast.error(e.message); }
  };

  const ask = async () => {
    if (!askQ.trim()) return;
    const q = askQ; setAskQ(''); setAsking(true);
    setAskLog(prev => [...prev, { role: 'user', text: q }]);
    try {
      const r = await api(`/api/admin/blueprint-ai/projects/${projectId}/ask`, { method: 'POST', body: JSON.stringify({ question: q }) });
      setAskLog(prev => [...prev, { role: 'ai', text: r.answer }]);
    } catch (e) { setAskLog(prev => [...prev, { role: 'ai', text: `Error: ${e.message}` }]); }
    finally { setAsking(false); }
  };

  const loadAudit = async () => {
    try { setAudit(await api(`/api/admin/blueprint-ai/projects/${projectId}/audit`)); } catch { /* */ }
  };

  const exportPdf = async () => {
    let b = boq;
    if (!b) {
      try { b = await api(`/api/admin/blueprint-ai/projects/${projectId}/boq`); setBoq(b); } catch { b = null; }
    }
    buildReport(project, b).save(`${project.name.replace(/\s+/g, '_')}_report.pdf`);
  };

  const exportBoqCsv = () => {
    if (!boq) return;
    const rows = [['Item', 'Qty', 'Unit', 'Material $/u', 'Labor $/u', 'Line Total'],
      ...[...(boq.device_lines || []), ...(boq.wire_lines || [])].map(l => [l.description, l.qty, l.unit, l.material_unit, l.labor_unit, l.line_total]),
      [], ['Material subtotal', boq.subtotal_material], ['Labor subtotal', boq.subtotal_labor], ['Grand total', boq.grand_total]];
    const csv = rows.map(r => r.map(v => `"${String(v ?? '')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `${project.name.replace(/\s+/g, '_')}_BOQ.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/blueprint-ai/projects/${projectId}/export-excel`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Excel export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${project.name.replace(/\s+/g, '_')}_takeoff.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch (e) { toast.error(e.message); }
  };

  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const createInvoice = async () => {
    setCreatingInvoice(true);
    try {
      const r = await api(`/api/admin/blueprint-ai/projects/${projectId}/create-invoice`, { method: 'POST' });
      toast.success(`Invoice ${r.invoice_number} created ($${r.total}) — assign a customer & send it from the Invoices page`);
    } catch (e) { toast.error(e.message); }
    finally { setCreatingInvoice(false); }
  };

  const sendReport = async () => {
    setSending(true);
    try {
      const pdfB64 = buildReport(project, boq).output('datauristring').split('base64,')[1];
      await api(`/api/admin/blueprint-ai/projects/${projectId}/email`, {
        method: 'POST', body: JSON.stringify({ ...email, pdf_base64: pdfB64 })
      });
      toast.success('Report emailed!'); setShowEmail(false);
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  if (!project) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div>;

  const st = PROJECT_STATUS[project.status] || PROJECT_STATUS.created;
  const pageDevices = (project.devices || []).filter(d => d.page === page)
    .filter(d => markerFilter === 'all' || (markerFilter === 'issues' && d.status === 'needs_review'))
    .filter(d => !typeFilter || d.type === typeFilter)
    .filter(d => !circuitFilter || circuitFilter.ids.includes(d.id));
  const imgKey = `${project.current_version}-${page}`;
  const filteredFindings = (project.findings || []).filter(f => findingFilter === 'all' || f.status === findingFilter);
  const inputCls = 'input text-sm py-2';

  return (
    <div className="space-y-4" data-testid="bpai-workspace">
      {/* Top bar */}
      <div className="card p-4 flex flex-wrap items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-800 rounded-lg" data-testid="bpai-back-btn"><ArrowLeft className="w-5 h-5 text-gray-400" /></button>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-xl font-bold text-white">{project.name}</h1>
          <p className="text-xs text-gray-500">{project.address} · v{project.current_version} · {project.page_count} pages
            {project.total_sqft?.value ? ` · ${project.total_sqft.value} sqft (${project.total_sqft.source})` : ''}
            {project.scale ? ` · Scale: ${project.scale}` : ''}</p>
        </div>
        <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${st.cls}`} data-testid="bpai-status">{st.label}
          {project.status === 'processing' && ` · ${PHASE_LABELS[project.analysis_phase] || 'Starting'} ${project.phase_page || 0}/${project.phase_total || project.page_count}${project.eta_seconds ? ` · ~${Math.max(1, Math.ceil(project.eta_seconds / 60))} min left` : ''}`}</span>
        <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={e => upload(e.target.files[0])} data-testid="bpai-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 border border-gray-700 rounded-lg text-sm text-gray-300 hover:border-red-500" data-testid="bpai-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload {project.page_count > 0 ? 'New Version' : 'Blueprint'}
        </button>
        <button onClick={analyze} disabled={project.status === 'processing' || project.page_count === 0} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="bpai-analyze-btn">
          {project.status === 'processing' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />} Analyze with AI
        </button>
        <button onClick={exportPdf} disabled={project.status !== 'analyzed'} className="p-2.5 border border-gray-700 rounded-lg text-gray-300 hover:border-red-500 disabled:opacity-40" title="Export PDF" data-testid="bpai-pdf-btn"><FileDown className="w-4 h-4" /></button>
        <button onClick={exportExcel} disabled={project.status !== 'analyzed'} className="p-2.5 border border-gray-700 rounded-lg text-gray-300 hover:border-red-500 disabled:opacity-40" title="Export Excel (takeoff + BOQ + wiring)" data-testid="bpai-excel-btn"><FileSpreadsheet className="w-4 h-4" /></button>
        {(project.versions || []).length > 1 && (
          <button onClick={loadCompare} className="p-2.5 border border-gray-700 rounded-lg text-gray-300 hover:border-red-500 text-xs font-bold" title="Compare versions" data-testid="bpai-compare-btn">
            v{project.versions[project.versions.length - 2].version}↔v{project.current_version}
          </button>
        )}
        <button onClick={() => setShowEmail(true)} disabled={project.status !== 'analyzed'} className="p-2.5 border border-gray-700 rounded-lg text-gray-300 hover:border-red-500 disabled:opacity-40" title="Email report" data-testid="bpai-email-btn"><Mail className="w-4 h-4" /></button>
      </div>

      {project.status === 'error' && (
        <div className="card p-4 border-red-500/40 text-red-400 text-sm flex items-center gap-2" data-testid="bpai-error">
          <AlertTriangle className="w-4 h-4" /> {project.analysis_error} — try Analyze again.
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* Left: pages */}
        <div className="col-span-12 lg:col-span-2 space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1"><Layers className="w-3 h-3" /> Pages ({(project.pages || []).length})</p>
          {(project.pages || []).some(pg => pg.discipline) && (
            <select value={disciplineFilter} onChange={e => setDisciplineFilter(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-300" data-testid="bpai-discipline-filter">
              <option value="all">All disciplines</option>
              {[...new Set((project.pages || []).map(pg => pg.discipline).filter(Boolean))].map(d => (
                <option key={d} value={d}>{DISCIPLINE_META[d]?.label || d}</option>
              ))}
            </select>
          )}
          {project.pages?.length === 0 && <p className="text-xs text-gray-600">No blueprint uploaded yet.</p>}
          <div className="space-y-2 overflow-y-auto" style={{ maxHeight: '62vh' }}>
          {(project.pages || []).filter(pg => disciplineFilter === 'all' || pg.discipline === disciplineFilter).map(pg => {
            const dm = DISCIPLINE_META[pg.discipline];
            return (
            <button key={pg.page} onClick={() => { setPage(pg.page); setSelectedDevice(null); }}
              className={`w-full text-left p-2.5 rounded-lg border text-xs transition-colors ${page === pg.page ? 'border-red-500 bg-red-500/10 text-white' : 'border-gray-800 text-gray-400 hover:border-gray-600'}`}
              data-testid={`bpai-page-${pg.page}`}>
              <span className="font-bold">P{pg.page}</span> {pg.label && <span className="text-gray-500">· {pg.label}</span>}
              {pg.has_electrical === true && <span className="ml-1 text-green-400">⚡</span>}
              <div className="flex flex-wrap gap-1 mt-1">
                {dm && <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${dm.cls}`}>{dm.label}</span>}
                {pg.voltage_class && pg.voltage_class !== 'none' && (
                  <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-gray-700 text-gray-300">{pg.voltage_class.replace('_', ' ').toUpperCase()}</span>
                )}
              </div>
              {pg.summary && <p className="text-[10px] text-gray-600 mt-0.5 line-clamp-2">{pg.summary}</p>}
            </button>
            );
          })}
          </div>
        </div>

        {/* Center: viewer */}
        <div className="col-span-12 lg:col-span-6">
          <div className="card overflow-hidden">
            <div className="flex flex-wrap items-center gap-2 p-2 border-b border-gray-800">
              <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} className="p-1.5 hover:bg-gray-800 rounded" data-testid="bpai-zoom-in"><ZoomIn className="w-4 h-4 text-gray-400" /></button>
              <button onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} className="p-1.5 hover:bg-gray-800 rounded" data-testid="bpai-zoom-out"><ZoomOut className="w-4 h-4 text-gray-400" /></button>
              <span className="text-xs text-gray-500">{Math.round(zoom * 100)}%</span>
              <div className="flex-1" />
              <select value={markerFilter} onChange={e => setMarkerFilter(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300" data-testid="bpai-marker-filter">
                <option value="all">Show All Devices</option>
                <option value="issues">Needs Review Only</option>
                <option value="none">Hide Markers</option>
              </select>
              {typeFilter && (
                <button onClick={() => setTypeFilter(null)} className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded-full">{typeLabel(typeFilter)} ✕</button>
              )}
              {circuitFilter && (
                <button onClick={() => setCircuitFilter(null)} className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full" data-testid="bpai-circuit-chip">⚡ {circuitFilter.label} ✕</button>
              )}
              <button onClick={() => setAddMode(!addMode)} className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border ${addMode ? 'border-red-500 text-red-400 bg-red-500/10' : 'border-gray-700 text-gray-400'}`} data-testid="bpai-add-mode-btn">
                <Crosshair className="w-3 h-3" /> {addMode ? 'Click on plan to place' : 'Add Device'}
              </button>
            </div>
            <div className="overflow-auto bg-gray-950" style={{ maxHeight: '68vh' }}>
              {images[imgKey] ? (
                <div className={`relative inline-block ${addMode ? 'cursor-crosshair' : ''}`} style={{ width: `${zoom * 100}%` }} onClick={planClick} data-testid="bpai-plan-canvas">
                  <img src={images[imgKey]} alt={`Page ${page}`} className="w-full select-none" draggable={false} />
                  {markerFilter !== 'none' && pageDevices.map(d => (
                    <button key={d.id}
                      onClick={(e) => { e.stopPropagation(); setSelectedDevice(d); setTab('device'); }}
                      className="absolute rounded-full border-2 border-white shadow-lg hover:scale-125 transition-transform"
                      style={{
                        left: `${d.x_pct}%`, top: `${d.y_pct}%`, width: 14, height: 14, transform: 'translate(-50%, -50%)',
                        backgroundColor: MARKER_COLORS[d.status] || MARKER_COLORS.ai_detected,
                        outline: selectedDevice?.id === d.id ? '3px solid #E31B23' : 'none'
                      }}
                      title={`${typeLabel(d.type)} — ${d.room || 'unknown'} (${d.confidence}%)`}
                      data-testid={`bpai-marker-${d.id}`} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-24 text-gray-600">
                  {project.page_count > 0 ? <Loader2 className="w-8 h-8 animate-spin" /> : (
                    <>
                      <Upload className="w-10 h-10 mb-3" />
                      <p className="text-sm">Upload a blueprint PDF to begin</p>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 p-2 border-t border-gray-800 text-[10px] text-gray-500">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: MARKER_COLORS.verified }} /> Verified</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: MARKER_COLORS.ai_detected }} /> AI Detected</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: MARKER_COLORS.needs_review }} /> Needs Review</span>
              <span className="ml-auto">{pageDevices.length} devices on this page</span>
            </div>
          </div>
        </div>

        {/* Right: panels */}
        <div className="col-span-12 lg:col-span-4">
          <div className="card">
            <div className="flex border-b border-gray-800 overflow-x-auto">
              {[['overview', LayoutDashboard, 'Overview'], ['takeoff', ClipboardList, 'Takeoff'], ['device', MapPin, 'Device'], ['findings', AlertTriangle, 'Code'],
                ['circuits', Zap, 'Circuits'], ['calcs', Calculator, 'Calcs'], ['sld', Network, 'SLD'], ['boq', DollarSign, 'BOQ'], ['wiring', Cable, 'Wiring'],
                ['legend', Eye, 'Legend'], ['ask', MessageSquare, 'Ask'], ['audit', History, 'Audit']].map(([id, Icon, label]) => (
                <button key={id} onClick={() => { setTab(id); if (id === 'audit') loadAudit(); if (id === 'boq') loadBoq(); if (id === 'wiring') loadWiring(); }}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold whitespace-nowrap ${tab === id ? 'text-red-400 border-b-2 border-red-500' : 'text-gray-500 hover:text-gray-300'}`}
                  data-testid={`bpai-tab-${id}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>
            <div className="p-4 overflow-y-auto" style={{ maxHeight: '68vh' }}>

              {tab === 'overview' && (
                <div className="space-y-4" data-testid="bpai-overview">
                  {project.status !== 'analyzed' ? (
                    <p className="text-sm text-gray-500">Upload a blueprint and run the AI analysis to see the full project overview.</p>
                  ) : (() => {
                    const score = (() => {
                      const p0 = project.passes || 0, f = project.issues || 0, w = project.warnings || 0, nr = project.needs_review || 0;
                      const tot = p0 + f + w * 0.5 + nr * 0.5;
                      return tot > 0 ? Math.round((p0 / tot) * 100) : null;
                    })();
                    const topTypes = (project.takeoff || []).slice(0, 6);
                    const maxQty = Math.max(1, ...topTypes.map(t => t.total));
                    return (
                      <>
                        <div className="flex items-center gap-4">
                          <div className="relative w-20 h-20 shrink-0">
                            <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
                              <circle cx="40" cy="40" r="34" fill="none" stroke="#1f2937" strokeWidth="8" />
                              <circle cx="40" cy="40" r="34" fill="none"
                                stroke={score === null ? '#4b5563' : score >= 80 ? '#22c55e' : score >= 50 ? '#eab308' : '#ef4444'}
                                strokeWidth="8" strokeLinecap="round"
                                strokeDasharray={`${((score || 0) / 100) * 213.6} 213.6`} />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-white" data-testid="bpai-compliance-score">
                              {score === null ? '—' : `${score}%`}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">Code Compliance Score</p>
                            <p className="text-[11px] text-gray-500">{project.passes} pass · {project.issues} fail · {project.warnings} warning · {project.needs_review} review</p>
                            {calcs?.load_calc?.recommended_service && (
                              <p className="text-xs mt-1 text-gray-300">Recommended service: <b className="text-red-400">{calcs.load_calc.recommended_service}</b> ({calcs.load_calc.calculated_amps}A calc.)</p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            ['Devices', project.device_count, MapPin],
                            ['Pages', project.page_count, Layers],
                            ['Sq Ft', project.total_sqft?.value || '—', LayoutDashboard],
                            ['Wire LF', wiring?.total_lf ?? '…', Cable],
                            ['Estimate', boq ? `$${(boq.grand_total || 0).toLocaleString()}` : '…', DollarSign],
                            ['Legend Symbols', (project.legend || []).length, Eye],
                          ].map(([label, val, Icon]) => (
                            <div key={label} className="border border-gray-800 rounded-lg p-3 flex items-center gap-2.5">
                              <Icon className="w-4 h-4 text-red-400 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-base font-bold text-white truncate">{val}</p>
                                <p className="text-[10px] text-gray-500 uppercase">{label}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {topTypes.length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Top Detected Items</p>
                            <div className="space-y-1.5">
                              {topTypes.map(t => (
                                <div key={t.type} className="flex items-center gap-2 text-xs">
                                  <span className="w-28 truncate text-gray-400">{typeLabel(t.type)}</span>
                                  <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
                                    <div className="h-2 bg-red-500/70 rounded-full" style={{ width: `${(t.total / maxQty) * 100}%` }} />
                                  </div>
                                  <span className="w-8 text-right font-bold text-white">{t.total}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(project.ai_notes || []).length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">AI Notes ({project.ai_notes.length})</p>
                            <ul className="space-y-1 max-h-32 overflow-y-auto">
                              {project.ai_notes.slice(0, 10).map((n, i) => (
                                <li key={i} className="text-[11px] text-gray-400 border-l-2 border-yellow-500/40 pl-2">{n}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {tab === 'takeoff' && (
                <div data-testid="bpai-takeoff">
                  {(project.takeoff || []).length === 0 ? <p className="text-sm text-gray-500">Run the AI analysis to generate the electrical takeoff.</p> : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-[10px] text-gray-500 uppercase">
                        <th className="text-left pb-2">Item</th><th className="text-right pb-2">Qty</th>
                        <th className="text-right pb-2">Verified</th><th className="text-right pb-2">Review</th></tr></thead>
                      <tbody className="divide-y divide-gray-800">
                        {project.takeoff.map(r => (
                          <tr key={r.type} onClick={() => setTypeFilter(typeFilter === r.type ? null : r.type)}
                            className={`cursor-pointer hover:bg-gray-800/50 ${typeFilter === r.type ? 'bg-red-500/10' : ''}`}
                            data-testid={`bpai-takeoff-${r.type}`}>
                            <td className="py-2 text-gray-300">{typeLabel(r.type)}</td>
                            <td className="py-2 text-right font-bold text-white">{r.total}</td>
                            <td className="py-2 text-right text-green-400">{r.verified}</td>
                            <td className="py-2 text-right text-yellow-400">{r.needs_review}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {(project.takeoff_by_tag || []).length > 0 && (
                    <div className="mt-4" data-testid="bpai-takeoff-tags">
                      <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Counts by Fixture Tag</p>
                      <table className="w-full text-xs">
                        <thead><tr className="text-[9px] text-gray-500 uppercase">
                          <th className="text-left pb-1">Tag</th><th className="text-left pb-1">Type(s)</th><th className="text-right pb-1">Qty</th></tr></thead>
                        <tbody className="divide-y divide-gray-800/60">
                          {project.takeoff_by_tag.map(t => (
                            <tr key={t.tag}>
                              <td className="py-1.5 font-mono font-bold text-yellow-400">{t.tag}</td>
                              <td className="py-1.5 text-gray-400">{(t.types || []).map(typeLabel).join(', ')}</td>
                              <td className="py-1.5 text-right font-bold text-white">{t.total}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {(project.rooms || []).length > 0 && (
                    <div className="mt-4">
                      <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Rooms / Square Footage</p>
                      {project.rooms.map((r, i) => (
                        <div key={i} className="flex justify-between text-xs py-1 border-b border-gray-800/60">
                          <span className="text-gray-300">{r.name} <span className="text-gray-600">(P{r.page})</span></span>
                          <span className="text-gray-400">{r.sqft ? `${r.sqft} sqft` : '—'} <span className="text-gray-600">· {r.sqft_source}</span></span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {tab === 'device' && (
                !selectedDevice ? <p className="text-sm text-gray-500">Click a marker on the blueprint to inspect a device, or use "Add Device".</p> : (
                  <div className="space-y-3" data-testid="bpai-device-panel">
                    <div className="flex items-center justify-between">
                      <span className="text-xs px-2 py-1 rounded-full font-bold" style={{ background: (MARKER_COLORS[selectedDevice.status] || '#3b82f6') + '22', color: MARKER_COLORS[selectedDevice.status] || '#3b82f6' }}>
                        {selectedDevice.status?.replace('_', ' ')}
                      </span>
                      <span className="text-xs text-gray-500">AI confidence: <b className="text-white">{selectedDevice.confidence}%</b> ({confLabel(selectedDevice.confidence)})</span>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase font-bold">Type</label>
                      <select value={selectedDevice.type} onChange={e => setSelectedDevice({ ...selectedDevice, type: e.target.value })} className={inputCls} data-testid="bpai-device-type">
                        {DEVICE_TYPES.map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[10px] text-gray-500 uppercase font-bold">Room</label>
                        <input value={selectedDevice.room || ''} onChange={e => setSelectedDevice({ ...selectedDevice, room: e.target.value })} className={inputCls} data-testid="bpai-device-room" /></div>
                      <div><label className="text-[10px] text-gray-500 uppercase font-bold">Floor</label>
                        <input value={selectedDevice.floor || ''} onChange={e => setSelectedDevice({ ...selectedDevice, floor: e.target.value })} className={inputCls} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-[10px] text-gray-500 uppercase font-bold">Quantity</label>
                        <input type="number" value={selectedDevice.quantity || 1} onChange={e => setSelectedDevice({ ...selectedDevice, quantity: parseInt(e.target.value) || 1 })} className={inputCls} data-testid="bpai-device-qty" /></div>
                      <div><label className="text-[10px] text-gray-500 uppercase font-bold">Page · Position</label>
                        <p className="text-sm text-gray-300 py-2">P{selectedDevice.page} · {selectedDevice.x_pct?.toFixed?.(1)}%, {selectedDevice.y_pct?.toFixed?.(1)}%</p></div>
                    </div>
                    <div><label className="text-[10px] text-gray-500 uppercase font-bold">Notes</label>
                      <textarea value={selectedDevice.notes || ''} onChange={e => setSelectedDevice({ ...selectedDevice, notes: e.target.value })} rows={2} className={inputCls} /></div>
                    <p className="text-[10px] text-gray-600">Source: {selectedDevice.source}{selectedDevice.tag ? ` · tag: ${selectedDevice.tag}` : ''} {selectedDevice.label ? `· plan label: "${selectedDevice.label}"` : ''}</p>
                    <div className="flex gap-2">
                      <button onClick={() => saveDevice({ type: selectedDevice.type, room: selectedDevice.room, floor: selectedDevice.floor, quantity: selectedDevice.quantity, notes: selectedDevice.notes })} className="btn-primary flex-1 text-sm py-2" data-testid="bpai-device-save">Save</button>
                      {selectedDevice.status !== 'verified' && (
                        <button onClick={() => saveDevice({ status: 'verified' }, 'Confirmed by admin')} className="flex-1 text-sm py-2 rounded-lg bg-green-600/20 text-green-400 border border-green-600/40 font-bold" data-testid="bpai-device-verify">
                          <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Verify
                        </button>
                      )}
                      <button onClick={deleteDevice} className="p-2 text-red-400 border border-gray-700 rounded-lg" data-testid="bpai-device-delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                )
              )}

              {tab === 'findings' && (
                <div className="space-y-3" data-testid="bpai-findings">
                  <select value={findingFilter} onChange={e => setFindingFilter(e.target.value)} className={inputCls} data-testid="bpai-finding-filter">
                    <option value="all">All findings ({(project.findings || []).length})</option>
                    <option value="FAIL">FAIL ({project.issues})</option>
                    <option value="WARNING">WARNING ({project.warnings})</option>
                    <option value="NEEDS_REVIEW">NEEDS REVIEW ({project.needs_review})</option>
                    <option value="PASS">PASS ({project.passes})</option>
                  </select>
                  {filteredFindings.length === 0 && <p className="text-sm text-gray-500">No code findings {findingFilter !== 'all' ? 'with this status' : 'yet — run the AI analysis'}.</p>}
                  {filteredFindings.map(f => (
                    <div key={f.id} className="border border-gray-800 rounded-lg p-3 space-y-2" data-testid={`bpai-finding-${f.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-bold text-white">{f.title}</p>
                        <div className="flex items-center gap-1.5">
                          {f.source === 'rules_engine' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/30">RULES ENGINE</span>}
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_BADGE[f.status] || ''}`}>{f.status}</span>
                        </div>
                      </div>
                      <p className="text-[11px] text-gray-500">{f.code_name} {f.code_edition} §{f.code_section} · {f.room || '—'} · Page {f.page} · Confidence {f.confidence}%</p>
                      {(f.measured_value || f.required_value) && (
                        <p className="text-[11px] text-gray-400">Measured: <b>{f.measured_value || '—'}</b> · Required: <b>{f.required_value || '—'}</b></p>
                      )}
                      <p className="text-xs text-gray-400"><b className="text-gray-300">Requirement:</b> {f.requirement}</p>
                      <p className="text-xs text-gray-400"><b className="text-gray-300">Why:</b> {f.explanation}</p>
                      <p className="text-xs text-gray-500"><b className="text-gray-400">Evidence:</b> {f.evidence}</p>
                      {f.admin_decision && <p className="text-xs text-blue-400">Admin: {f.admin_decision} {f.admin_comment && `— ${f.admin_comment}`}</p>}
                      <div className="flex gap-2">
                        <select defaultValue="" onChange={e => { if (e.target.value) updateFinding(f, { status: e.target.value, admin_decision: 'override', admin_comment: 'Status changed by admin' }); }}
                          className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300" data-testid={`bpai-finding-status-${f.id}`}>
                          <option value="">Override status...</option>
                          {['PASS', 'FAIL', 'WARNING', 'NEEDS_REVIEW', 'RESOLVED'].filter(s => s !== f.status).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {f.status !== 'RESOLVED' && (
                          <button onClick={() => updateFinding(f, { status: 'RESOLVED', admin_decision: 'resolved' })} className="text-xs px-3 py-1 bg-blue-600/20 text-blue-400 border border-blue-600/40 rounded font-bold">Resolve</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'circuits' && (
                <div className="space-y-4" data-testid="bpai-circuits">
                  {(project.panels || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No panel schedules detected on this blueprint. Upload a plan that includes a panel/circuit schedule and re-analyze.</p>
                  ) : (project.panels || []).map(pn => (
                    <div key={pn.id} className="border border-gray-800 rounded-lg overflow-hidden">
                      <div className="p-3 bg-gray-800/60">
                        <p className="text-sm font-bold text-white flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" /> {pn.panel_name}</p>
                        <p className="text-[11px] text-gray-500">{pn.location || '—'} {pn.main_breaker ? `· Main: ${pn.main_breaker}` : ''} · Page {pn.page}</p>
                      </div>
                      <table className="w-full text-xs">
                        <thead><tr className="text-[9px] text-gray-500 uppercase border-b border-gray-800">
                          <th className="text-left px-3 py-1.5">#</th><th className="text-left px-2 py-1.5">Description</th>
                          <th className="text-left px-2 py-1.5">Brkr</th><th className="text-left px-2 py-1.5">Prot.</th>
                          <th className="text-right px-3 py-1.5">Linked</th></tr></thead>
                        <tbody className="divide-y divide-gray-800/60">
                          {(pn.circuits || []).map((c, i) => (
                            <tr key={i}
                              onClick={() => (c.linked_device_ids || []).length > 0 && setCircuitFilter({ label: `Ckt ${c.circuit}: ${c.description}`, ids: c.linked_device_ids })}
                              className={`${(c.linked_device_ids || []).length > 0 ? 'cursor-pointer hover:bg-gray-800/50' : ''}`}
                              data-testid={`bpai-circuit-${pn.id}-${i}`}>
                              <td className="px-3 py-1.5 text-gray-400">{c.circuit}</td>
                              <td className="px-2 py-1.5 text-gray-300">{c.description}
                                {(c.linked_rooms || []).length > 0 && <span className="text-gray-600"> · {c.linked_rooms.join(', ')}</span>}</td>
                              <td className="px-2 py-1.5 text-gray-400">{c.breaker || '—'}</td>
                              <td className="px-2 py-1.5">
                                {c.protection && c.protection !== 'standard' && c.protection !== 'unknown'
                                  ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-400">{c.protection}</span>
                                  : <span className="text-gray-600">—</span>}
                              </td>
                              <td className="px-3 py-1.5 text-right">
                                {(c.linked_device_ids || []).length > 0
                                  ? <span className="text-blue-400 font-bold">{c.linked_device_ids.length} ⚲</span>
                                  : <span className="text-gray-600">0</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  {(project.panels || []).length > 0 && <p className="text-[10px] text-gray-600">Click a circuit with linked devices to highlight them on the plan.</p>}
                </div>
              )}

              {tab === 'calcs' && (
                <div className="space-y-5" data-testid="bpai-calcs">
                  {!calcs ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div> : (
                    <>
                      <div>
                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Service Load Calculation — NEC Article 220</p>
                        <table className="w-full text-xs">
                          <tbody className="divide-y divide-gray-800/60">
                            {[...calcs.load_calc.lines, ...calcs.load_calc.fixed_loads].map((l, i) => (
                              <tr key={i}>
                                <td className="py-1.5 text-gray-300">{l.item}<span className="block text-[10px] text-gray-600">{l.basis}</span></td>
                                <td className="py-1.5 text-right font-bold text-white whitespace-nowrap">{l.va.toLocaleString()} VA</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="border-t border-gray-700 mt-2 pt-2 space-y-1 text-xs">
                          <div className="flex justify-between text-gray-400"><span>General demand ({calcs.load_calc.demand_note})</span><span>{calcs.load_calc.general_demand_va.toLocaleString()} VA</span></div>
                          <div className="flex justify-between text-white font-bold"><span>Total demand</span><span>{calcs.load_calc.total_demand_va.toLocaleString()} VA</span></div>
                          <div className="flex justify-between text-gray-400"><span>Calculated amps @ {calcs.load_calc.voltage_basis}</span><span>{calcs.load_calc.calculated_amps} A</span></div>
                        </div>
                        <div className="mt-3 border border-red-500/40 bg-red-500/10 rounded-lg p-3 text-center" data-testid="bpai-service-size">
                          <p className="text-[10px] text-gray-400 uppercase font-bold">Recommended Service Size</p>
                          <p className="text-2xl font-bold text-red-400">{calcs.load_calc.recommended_service}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Voltage Drop & Wire Sizing — per circuit</p>
                        {(calcs.voltage_drop || []).length === 0 ? (
                          <p className="text-sm text-gray-500">No panel schedule circuits found — voltage drop needs a panel schedule on the plan.</p>
                        ) : (
                          <table className="w-full text-[11px]">
                            <thead><tr className="text-[9px] text-gray-500 uppercase">
                              <th className="text-left pb-1">Ckt</th><th className="text-left pb-1">Description</th>
                              <th className="text-right pb-1">Brkr</th><th className="text-right pb-1">Wire</th>
                              <th className="text-right pb-1">Len</th><th className="text-right pb-1">VD%</th>
                              <th className="text-right pb-1">St.</th></tr></thead>
                            <tbody className="divide-y divide-gray-800/60">
                              {calcs.voltage_drop.map((r, i) => (
                                <tr key={i} data-testid={`bpai-vd-${i}`}>
                                  <td className="py-1.5 text-gray-400">{r.circuit}</td>
                                  <td className="py-1.5 text-gray-300 max-w-[110px] truncate" title={r.description}>{r.description}
                                    {r.undersized && <span className="block text-[9px] text-red-400 font-bold">Wire undersized — needs {r.required_wire}</span>}</td>
                                  <td className="py-1.5 text-right text-gray-400">{r.breaker}</td>
                                  <td className="py-1.5 text-right text-gray-400">{r.wire}</td>
                                  <td className="py-1.5 text-right text-gray-400">{r.length_ft}ft</td>
                                  <td className="py-1.5 text-right font-bold text-white">{r.vd_pct}%</td>
                                  <td className="py-1.5 text-right">
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${r.status === 'PASS' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>{r.status}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        <p className="text-[10px] text-gray-600 mt-2">{calcs.vd_note}</p>
                      </div>
                      {(calcs.load_calc.assumptions || []).length > 0 && (
                        <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-lg p-2.5">
                          <p className="text-[10px] font-bold text-yellow-400 uppercase mb-1">Assumptions — verify before permit use</p>
                          {calcs.load_calc.assumptions.map((a, i) => <p key={i} className="text-[11px] text-gray-400">· {a}</p>)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {tab === 'sld' && (
                <div data-testid="bpai-sld">
                  {(project.panels || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No panel schedules detected — the single-line diagram is auto-generated from panel data after analysis.</p>
                  ) : (() => {
                    const panels = project.panels;
                    let yPos = 110;
                    const blocks = panels.map(pn => {
                      const circuits = (pn.circuits || []).slice(0, 30);
                      const start = yPos;
                      yPos += 54 + circuits.length * 22 + 24;
                      return { pn, circuits, start };
                    });
                    const H = yPos + 10;
                    return (
                      <svg viewBox={`0 0 340 ${H}`} className="w-full" style={{ maxHeight: '60vh' }}>
                        <circle cx="40" cy="22" r="14" fill="none" stroke="#9ca3af" strokeWidth="1.5" />
                        <text x="40" y="26" textAnchor="middle" fill="#9ca3af" fontSize="9" fontWeight="bold">U</text>
                        <text x="62" y="26" fill="#9ca3af" fontSize="10">UTILITY SERVICE</text>
                        <line x1="40" y1="36" x2="40" y2="62" stroke="#9ca3af" strokeWidth="1.5" />
                        <rect x="28" y="62" width="24" height="24" fill="none" stroke="#9ca3af" strokeWidth="1.5" />
                        <text x="40" y="78" textAnchor="middle" fill="#9ca3af" fontSize="8" fontWeight="bold">kWh</text>
                        <text x="62" y="78" fill="#9ca3af" fontSize="10">METER</text>
                        <line x1="40" y1="86" x2="40" y2={blocks[0].start} stroke="#9ca3af" strokeWidth="1.5" />
                        {blocks.map(({ pn, circuits, start }, bi) => (
                          <g key={pn.id || bi}>
                            <rect x="12" y={start} width="120" height="40" rx="4" fill="#1f2937" stroke="#E31B23" strokeWidth="1.5" />
                            <text x="72" y={start + 17} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">{(pn.panel_name || 'PANEL').substring(0, 18)}</text>
                            <text x="72" y={start + 31} textAnchor="middle" fill="#9ca3af" fontSize="8">{pn.main_breaker ? `MAIN ${pn.main_breaker}` : 'MAIN'}</text>
                            <line x1="40" y1={start + 40} x2="40" y2={start + 44 + circuits.length * 22} stroke="#E31B23" strokeWidth="2" />
                            {circuits.map((c, ci) => {
                              const cy = start + 54 + ci * 22;
                              return (
                                <g key={ci}>
                                  <line x1="40" y1={cy} x2="76" y2={cy} stroke="#9ca3af" strokeWidth="1" />
                                  <path d={`M 76 ${cy} q 7 -7 14 0`} fill="none" stroke="#eab308" strokeWidth="1.3" />
                                  <text x="96" y={cy + 3} fill="#d1d5db" fontSize="8.5">
                                    #{c.circuit} · {c.breaker || '—'} · {(c.description || '').substring(0, 28)}
                                  </text>
                                </g>
                              );
                            })}
                            {bi < blocks.length - 1 && (
                              <line x1="40" y1={start + 44 + circuits.length * 22} x2="40" y2={blocks[bi + 1].start} stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="4 3" />
                            )}
                          </g>
                        ))}
                      </svg>
                    );
                  })()}
                  {(project.panels || []).length > 0 && (
                    <p className="text-[10px] text-gray-600 mt-2">Single-line diagram auto-generated from the detected panel schedule(s). Breaker symbols shown per circuit.</p>
                  )}
                </div>
              )}

              {tab === 'boq' && (
                <div className="space-y-4" data-testid="bpai-boq">
                  {!boq ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>
                    : ((boq.device_lines || []).length === 0 && (boq.wire_lines || []).length === 0) ? (
                      <p className="text-sm text-gray-500">Run the AI analysis first — the Bill of Quantities is generated from the takeoff.</p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-bold text-gray-500 uppercase">Bill of Quantities — edit unit prices inline</p>
                          <button onClick={exportBoqCsv} className="flex items-center gap-1 text-[10px] font-bold text-gray-400 border border-gray-700 rounded px-2 py-1 hover:border-red-500" data-testid="bpai-boq-csv">
                            <FileSpreadsheet className="w-3 h-3" /> CSV
                          </button>
                        </div>
                        <table className="w-full text-xs">
                          <thead><tr className="text-[9px] text-gray-500 uppercase">
                            <th className="text-left pb-1">Item</th><th className="text-right pb-1">Qty</th>
                            <th className="text-right pb-1">Mat $/u</th><th className="text-right pb-1">Lab $/u</th>
                            <th className="text-right pb-1">Total</th></tr></thead>
                          <tbody className="divide-y divide-gray-800/60">
                            {[...(boq.device_lines || []), ...(boq.wire_lines || [])].map(l => (
                              <tr key={l.item_id + l.description} data-testid={`bpai-boq-${l.item_id}`}>
                                <td className="py-1.5 text-gray-300">{l.description}</td>
                                <td className="py-1.5 text-right text-white font-bold whitespace-nowrap">{l.qty} {l.unit}</td>
                                <td className="py-1.5 text-right">
                                  <input type="number" step="0.01" defaultValue={l.material_unit}
                                    onBlur={e => parseFloat(e.target.value) !== l.material_unit && savePrice(l, 'material_cost', e.target.value)}
                                    className="w-16 bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-right text-gray-300"
                                    data-testid={`bpai-boq-mat-${l.item_id}`} />
                                </td>
                                <td className="py-1.5 text-right">
                                  <input type="number" step="0.01" defaultValue={l.labor_unit}
                                    onBlur={e => parseFloat(e.target.value) !== l.labor_unit && savePrice(l, 'labor_cost', e.target.value)}
                                    className="w-16 bg-gray-800 border border-gray-700 rounded px-1 py-0.5 text-right text-gray-300"
                                    data-testid={`bpai-boq-lab-${l.item_id}`} />
                                </td>
                                <td className="py-1.5 text-right font-bold text-green-400">${(l.line_total || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="border-t border-gray-700 pt-3 space-y-1 text-sm">
                          <div className="flex justify-between text-gray-400"><span>Material subtotal</span><span>${(boq.subtotal_material || 0).toFixed(2)}</span></div>
                          <div className="flex justify-between text-gray-400"><span>Labor subtotal</span><span>${(boq.subtotal_labor || 0).toFixed(2)}</span></div>
                          <div className="flex justify-between text-white font-bold text-base"><span>Grand Total</span><span className="text-red-400" data-testid="bpai-boq-total">${(boq.grand_total || 0).toFixed(2)}</span></div>
                        </div>
                        <button onClick={createInvoice} disabled={creatingInvoice}
                          className="btn-primary w-full flex items-center justify-center gap-2 text-sm py-2.5 disabled:opacity-50" data-testid="bpai-create-invoice-btn">
                          {creatingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
                          Create Invoice from BOQ
                        </button>
                        <p className="text-[10px] text-gray-600">Creates a draft invoice with all BOQ lines — assign a customer and send it from the Invoices page.</p>
                        <p className="text-[10px] text-gray-600">{boq.wiring_note}</p>
                      </>
                    )}
                </div>
              )}

              {tab === 'wiring' && (
                <div className="space-y-3" data-testid="bpai-wiring">
                  {!wiring ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-red-500" /></div>
                    : (wiring.systems || []).length === 0 ? (
                      <p className="text-sm text-gray-500">Run the AI analysis first — the wiring estimate is derived from detected devices.</p>
                    ) : (
                      <>
                        <p className="text-[10px] font-bold text-gray-500 uppercase">Wiring Estimate {wiring.scale_used ? `· Scale: ${wiring.scale_used}` : '· No scale — average allowances'}</p>
                        {wiring.systems.map((s, i) => (
                          <div key={i} className="border border-gray-800 rounded-lg p-3" data-testid={`bpai-wiring-${s.system}-${i}`}>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-bold text-white capitalize">{s.system.replace('_', ' ')}{s.floor ? ` · Floor ${s.floor}` : ''}</p>
                              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${s.method === 'calculated' ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`}>
                                {s.method === 'calculated' ? 'CALCULATED' : 'AVG ALLOWANCE'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">{s.wire_type} · {s.devices} devices</p>
                            <p className="text-xs text-gray-300 mt-1">{s.linear_ft} LF <span className="text-gray-500">→ with 15% waste:</span> <b className="text-white">{s.with_waste_ft} LF</b></p>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm font-bold text-white border-t border-gray-700 pt-2">
                          <span>Total wire (incl. waste)</span><span className="text-red-400" data-testid="bpai-wiring-total">{wiring.total_lf} LF</span>
                        </div>
                        <p className="text-[10px] text-gray-600">{wiring.note}</p>
                      </>
                    )}
                </div>
              )}

              {tab === 'legend' && (
                <div className="space-y-4" data-testid="bpai-legend">
                  <div>
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Legend Symbols ({(project.legend || []).length})</p>
                    {(project.legend || []).length === 0 ? (
                      <p className="text-sm text-gray-500">No legend detected yet. Symbols defined in the plan legend appear here after analysis.</p>
                    ) : (
                      <div className="space-y-2">
                        {project.legend.map((ls, i) => (
                          <div key={i} className="border border-gray-800 rounded-lg p-2.5">
                            <p className="text-xs font-bold text-yellow-400 font-mono">{ls.symbol}</p>
                            <p className="text-xs text-gray-300 mt-0.5">{ls.meaning}</p>
                            <p className="text-[10px] text-gray-600 mt-0.5">
                              {ls.device_type ? `Type: ${typeLabel(ls.device_type)} · ` : ''}Page {ls.page}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {(project.fixture_schedule || []).length > 0 && (
                    <div data-testid="bpai-fixture-schedule">
                      <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">Fixture Schedule ({project.fixture_schedule.length})</p>
                      <div className="space-y-2">
                        {project.fixture_schedule.map((fx, i) => (
                          <div key={i} className="border border-gray-800 rounded-lg p-2.5 flex items-start justify-between gap-2">
                            <div>
                              <p className="text-xs font-bold text-yellow-400 font-mono">{fx.tag}</p>
                              <p className="text-xs text-gray-300 mt-0.5">{fx.description}</p>
                              <p className="text-[10px] text-gray-600 mt-0.5">
                                {[fx.manufacturer, fx.model, fx.voltage, fx.watts, fx.mounting].filter(Boolean).join(' · ') || `Page ${fx.page}`}
                              </p>
                            </div>
                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${(fx.drawn_count || 0) > 0 ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`}>
                              {(fx.drawn_count || 0) > 0 ? `${fx.drawn_count} drawn` : '0 drawn — verify'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(() => {
                    const cur = (project.pages || []).find(pg => pg.page === page);
                    const ann = cur?.annotations || [];
                    return ann.length > 0 ? (
                      <div>
                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-2">OCR Text & Notes — Page {page} ({ann.length})</p>
                        <ul className="space-y-1 max-h-64 overflow-y-auto">
                          {ann.map((a, i) => <li key={i} className="text-[11px] text-gray-400 border-l-2 border-gray-700 pl-2">{a}</li>)}
                        </ul>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {tab === 'ask' && (
                <div className="flex flex-col h-full" data-testid="bpai-ask">
                  <div className="flex-1 space-y-3 mb-3 min-h-[200px]">
                    {askLog.length === 0 && (
                      <div className="text-xs text-gray-500 space-y-1">
                        <p className="font-bold text-gray-400">Ask the Blueprint — examples:</p>
                        <p>· How many GFCI outlets are on this project?</p>
                        <p>· How many outlets are in the kitchen?</p>
                        <p>· Show all potential code violations.</p>
                        <p>· What is the square footage of the second floor?</p>
                      </div>
                    )}
                    {askLog.map((m, i) => (
                      <div key={i} className={`text-sm p-2.5 rounded-lg ${m.role === 'user' ? 'bg-red-500/10 text-white ml-6' : 'bg-gray-800 text-gray-300 mr-6'}`}>
                        {m.text}
                      </div>
                    ))}
                    {asking && <Loader2 className="w-4 h-4 animate-spin text-red-500" />}
                  </div>
                  <div className="flex gap-2">
                    <input value={askQ} onChange={e => setAskQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && ask()}
                      placeholder="Ask the blueprint..." className={inputCls} disabled={project.status !== 'analyzed'} data-testid="bpai-ask-input" />
                    <button onClick={ask} disabled={asking || project.status !== 'analyzed'} className="btn-primary px-4 py-2 text-sm disabled:opacity-50" data-testid="bpai-ask-btn">Ask</button>
                  </div>
                </div>
              )}

              {tab === 'audit' && (
                <div className="space-y-2" data-testid="bpai-audit">
                  <button onClick={loadAudit} className="text-xs text-gray-400 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
                  {audit.length === 0 && <p className="text-sm text-gray-500">No audit entries yet.</p>}
                  {audit.map((a, i) => (
                    <div key={i} className="border-l-2 border-red-500/40 pl-3 py-1">
                      <p className="text-xs text-white font-semibold">{a.action.replace(/_/g, ' ')} <span className="text-gray-500 font-normal">· {a.user_name}</span></p>
                      <p className="text-[10px] text-gray-500">{new Date(a.timestamp).toLocaleString()}</p>
                      {(a.old_value || a.new_value) && (
                        <p className="text-[11px] text-gray-400 break-all">
                          {a.old_value ? <>from <span className="text-red-400">{JSON.stringify(a.old_value)}</span> </> : null}
                          {a.new_value ? <>→ <span className="text-green-400">{JSON.stringify(a.new_value)}</span></> : null}
                        </p>
                      )}
                      {a.comment && <p className="text-[11px] text-gray-500 italic">"{a.comment}"</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showEmail && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowEmail(false)}>
          <div className="card p-6 w-full max-w-md space-y-3" onClick={e => e.stopPropagation()} data-testid="bpai-email-modal">
            <h3 className="text-lg font-bold text-white">Send Report by Email</h3>
            <input type="email" className="input" placeholder="Recipient email *" value={email.to_email} onChange={e => setEmail({ ...email, to_email: e.target.value })} data-testid="bpai-email-to" />
            <input className="input" placeholder="Subject (optional)" value={email.subject} onChange={e => setEmail({ ...email, subject: e.target.value })} />
            <textarea className="input" rows={3} placeholder="Message (optional)" value={email.message} onChange={e => setEmail({ ...email, message: e.target.value })} />
            <button onClick={sendReport} disabled={sending || !email.to_email} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="bpai-email-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send Report
            </button>
          </div>
        </div>
      )}
      {compare && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setCompare(null)}>
          <div className="card p-6 w-full max-w-lg space-y-3" onClick={e => e.stopPropagation()} data-testid="bpai-compare-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Revision Comparison — v{compare.v_from} → v{compare.v_to}</h3>
              <button onClick={() => setCompare(null)} className="p-1 hover:bg-gray-800 rounded" data-testid="bpai-compare-close"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="flex gap-4 text-sm">
              <span className="text-green-400 font-bold">+{compare.summary?.added || 0} added</span>
              <span className="text-red-400 font-bold">−{compare.summary?.removed || 0} removed</span>
            </div>
            {(compare.changes || []).length === 0 ? (
              <p className="text-sm text-gray-500">No device quantity changes between these versions.</p>
            ) : (
              <div className="max-h-80 overflow-y-auto divide-y divide-gray-800">
                {compare.changes.map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-2 text-xs">
                    <span className="text-gray-300">{typeLabel(c.type)}{c.room ? <span className="text-gray-500"> · {c.room}</span> : null}</span>
                    <span className="whitespace-nowrap">
                      <span className="text-gray-500">{c.from} → {c.to}</span>
                      <span className={`ml-2 font-bold ${c.delta > 0 ? 'text-green-400' : 'text-red-400'}`}>{c.delta > 0 ? `+${c.delta}` : c.delta}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminBlueprintReader = () => {
  useLang();
  const [openProject, setOpenProject] = useState(null);
  return openProject
    ? <ProjectWorkspace projectId={openProject} onBack={() => setOpenProject(null)} />
    : <ProjectDashboard onOpen={setOpenProject} />;
};

export default AdminBlueprintReader;
